import { SERVICES, NATIVE_APP_AGENT } from '../../../../constants';
import { APP_RUNTIME } from '@/types/enums/common';
import {
	type AppCustomConfig,
	type StatusBarStyle,
	HapticFeedbackTypes
} from '@/types/interfaces/app';
import type { IService } from '@/types/interfaces/common';
import type { TCallback } from '@/utils/plugins/di/types';
import { CommonApplication } from './common-application.provider';
import type { Town } from '@/types/interfaces/config';
import {
	pushGateway,
	type RegisterTokenOptions,
	type SendPushEventOptions
} from '@/api';
import { STORAGE_PUSH_KEY } from '@/utils/constants/storage-push-key';

if (window.ReactNativeWebView && !window.ReactNativeWeb) {
	window.ReactNativeWeb = {
		didBecomeActive: () => {},
		didScanQrText: () => {},
		onCameraPermissionDenied: () => {},
		onBackPressed: () => {},
		onHandlePushToken: async (options: RegisterTokenOptions) => {
			const storedOptions = localStorage.getItem(STORAGE_PUSH_KEY);

			const prev = JSON.parse(storedOptions || '{}');
			if (prev.token === options.token) {
				return;
			}

			try {
				await pushGateway.registerToken(options);
				localStorage.setItem(STORAGE_PUSH_KEY, JSON.stringify(options));
			} catch (error) {
				console.error('Failed to register push token:', error);
			}
		},
		onOpenNativeNotification: async (
			options: SendPushEventOptions,
			url: string
		) => {
			try {
				await pushGateway.sendPushEvent(options);
			} catch (error) {
				console.error('Failed to register push token:', error);
			}

			window.location.href = url;
		},
		onLiveActivityStarted: () => {},
		onLiveActivityError: () => {},
		onLiveActivityTokenUpdated: () => {},
		onLiveActivityEnded: () => {}
	};
}

const backHandlersStack: Array<() => void> = [];

class ReactNativeApplication extends CommonApplication {
	constructor() {
		super(APP_RUNTIME.REACT_NATIVE);
	}

	private connect = <T>(callback: Function): Promise<T> => {
		return new Promise<T>((resolve, reject) => {
			try {
				callback(resolve, reject);
			} catch (error) {
				reject(error);
			}
		});
	};

	init() {
		const config = this.getCustomConfig();
		if (config && config.color) {
			document.documentElement.style.setProperty(
				'--primary-color',
				config.color
			);
		}
	}

	private filterValue(value?: string): string | undefined {
		if (value && value !== 'undefined') {
			return value;
		}
	}

	getCustomConfig(): AppCustomConfig | undefined {
		if (window.ReactNativeWebView) {
			return {
				...window.ReactNativeWebView,
				color: this.filterValue(window.ReactNativeWebView.color)
			};
		}
	}

	becomingActive(cb: TCallback) {
		// TODO: отделить ReactNativeWebView и Customization
		if (
			navigator.userAgent.includes(NATIVE_APP_AGENT) &&
			window.ReactNativeWeb
		) {
			window.ReactNativeWeb.didBecomeActive = cb;
		} else {
			document.addEventListener('visibilitychange', () => {
				this.onVisibilityChange(cb);
			});
		}
	}

	becomingHidden() {
		// TODO: отделить ReactNativeWebView и Customization
		if (
			navigator.userAgent.includes(NATIVE_APP_AGENT) &&
			window.ReactNativeWeb
		) {
			window.ReactNativeWeb.didBecomeActive = () => {};
		} else {
			document.removeEventListener(
				'visibilitychange',
				this.onVisibilityChange as any
			);
		}
	}

	handleNavigationToBack() {
		if (!window.ReactNativeWeb) {
			return false;
		}

		window.ReactNativeWeb.onBackPressed = function () {
			const responseToNative = backHandlersStack.length > 0;
			if (responseToNative) {
				const cb = backHandlersStack[backHandlersStack.length - 1];
				cb();
			}

			return responseToNative;
		};
	}

	whenNavigationToBack(cb: TCallback) {
		backHandlersStack.push(cb);
	}

	cancelHandlingToBack() {
		backHandlersStack.pop();
	}

	getServiceById(id: string, strict = false): IService {
		let result: IService | undefined;
		let config: AppCustomConfig | undefined;

		if (strict) {
			result = this.findServiceById(id);
		} else {
			config = this.getCustomConfig();
		}

		if (config) {
			if (config.services.includes(id)) {
				result = this.findServiceById(id);
			} else {
				if (config.services.includes(SERVICES.delivery.id)) {
					return SERVICES.delivery;
				}

				result = this.findServiceById(config.services[0]);
			}
		}

		return result || SERVICES.takeaway;
	}

	getVisibleTowns(towns: Town[]): Town[] {
		const config = this.getCustomConfig();

		if (!config) {
			return towns;
		}

		if (!config.town) {
			return towns;
		}

		if (Array.isArray(config.town)) {
			towns = towns.filter(
				t => config.town && config.town.includes(t.translit_name)
			);
		} else {
			towns = towns.filter(t => config.town === t.translit_name);
		}

		return towns;
	}

	openQR = (): Promise<string> => {
		return this.connect(
			(resolve: (text: string) => string, reject: (error: Error) => void) => {
				if (
					!window.ReactNativeWebView?.openQRScannerWithTextResult ||
					!window.ReactNativeWeb
				) {
					return resolve('');
				}

				window.ReactNativeWebView.openQRScannerWithTextResult();
				window.ReactNativeWeb.didScanQrText = text => resolve(text);
				window.ReactNativeWeb.onCameraPermissionDenied = text => {
					reject(new Error(text));
				};
			}
		);
	};

	changeLanguage = (lang: string): boolean => {
		if (!window.ReactNativeWebView?.changeLanguage) {
			return false;
		}

		window.ReactNativeWebView.changeLanguage(lang);
		return true;
	};

	setUserId = (id: string) => {
		if (window.ReactNativeWebView?.setUserID) {
			window.ReactNativeWebView.setUserID(id);
		}
	};

	clearUserId = () => {
		if (window.ReactNativeWebView?.clearUserID) {
			window.ReactNativeWebView.clearUserID();
		}
	};

	haptic = (type: HapticFeedbackTypes = HapticFeedbackTypes.impactLight) => {
		if (window.ReactNativeWebView?.haptic) {
			window.ReactNativeWebView.haptic(type);
		}
	};

	setStatusBarStyle = (style: StatusBarStyle) => {
		if (window.ReactNativeWebView?.setStatusBarStyle) {
			window.ReactNativeWebView.setStatusBarStyle(style);
		}
	};
}

export { ReactNativeApplication };
